/* eslint-disable max-lines */
import { Tooltip } from '@hakimo-ui/shared/ui-base';
import { ClockIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  AlertTriangle,
  FileText,
  ListOrdered,
  MicVocal,
  ShieldOff,
} from 'lucide-react';
import { ReactNode } from 'react';
import { useSectionState } from '../sop-workflow/SectionStateContext';
import {
  MUTED_ICON_CLASS,
  SOPCardDef,
  SOPCardId,
  SOPFreshness,
  SOPTabId,
} from './sop-card-utils';
import { CollapsibleCard } from './CollapsibleCard';
import { useSOPCardState } from './SOPCardStateContext';

const TAB_ITEMS = [
  { id: SOPTabId.SITUATIONS, name: 'Situations, Exceptions & Notes' },
  { id: SOPTabId.TALKDOWN_NOTES, name: 'Talkdown Notes' },
  { id: SOPTabId.ESCALATION_PROTOCOL, name: 'Escalation Protocol' },
];

const CARD_ICONS: Record<SOPCardId, ReactNode> = {
  [SOPCardId.SITUATIONS]: <AlertTriangle className={MUTED_ICON_CLASS} />,
  [SOPCardId.EXCEPTIONS]: <ShieldOff className={MUTED_ICON_CLASS} />,
  [SOPCardId.NOTES]: <FileText className={MUTED_ICON_CLASS} />,
  [SOPCardId.TALKDOWN_NOTES]: <MicVocal className={MUTED_ICON_CLASS} />,
  [SOPCardId.ESCALATION_PROTOCOL]: <ShieldOff className={MUTED_ICON_CLASS} />,
  [SOPCardId.ESCALATION_POINTS]: <ListOrdered className={MUTED_ICON_CLASS} />,
};

const CARD_SUBTITLES: Partial<Record<SOPCardId, string>> = {
  [SOPCardId.SITUATIONS]: '(Select if observed)',
  [SOPCardId.EXCEPTIONS]: '(Do not escalate if observed)',
};

interface Props {
  cards: SOPCardDef[];
  freshness: SOPFreshness;
  emptyMessage?: string;
}

export function SOPCard({ cards, freshness, emptyMessage }: Props) {
  const {
    isExpanded,
    isFullView,
    toggleCompressed,
    activeTab,
    setActiveTab,
    isCardCollapsed,
    toggleCardCollapsed,
  } = useSOPCardState();
  const { sections } = useSectionState();

  const nested = sections[SOPCardId.ESCALATION_PROTOCOL]?.isDetached
    ? undefined
    : cards.find(
        (card) => card.id === SOPCardId.ESCALATION_POINTS && !card.isEmpty
      );

  const visibleCards = cards.filter(
    (card) =>
      !card.isEmpty && (isFullView || card.tab === activeTab) && card !== nested
  );

  return (
    <CollapsibleCard
      variant="shell"
      title="SOP"
      collapsed={!isExpanded}
      onToggleCollapsed={toggleCompressed}
      titleAfter={<FreshnessClock freshness={freshness} />}
      below={
        <SOPTabs
          activeTabId={isFullView ? undefined : activeTab}
          onChange={setActiveTab}
        />
      }
    >
      <div className="flex flex-col gap-2.5">
        {emptyMessage ? (
          <div className="text-onlight-text-2 dark:text-ondark-text-2 p-3 text-sm">
            {emptyMessage}
          </div>
        ) : visibleCards.length === 0 ? (
          <div className="text-onlight-text-2 dark:text-ondark-text-2 p-3 text-sm">
            Nothing configured in this section.
          </div>
        ) : (
          visibleCards.map((card) => (
            <CollapsibleCard
              key={card.id}
              detachId={card.id}
              title={card.title}
              subtitle={CARD_SUBTITLES[card.id]}
              icon={CARD_ICONS[card.id]}
              collapsed={isCardCollapsed(card.id)}
              onToggleCollapsed={() => toggleCardCollapsed(card.id)}
            >
              {card.content}
              {card.id === SOPCardId.ESCALATION_PROTOCOL && nested && (
                <CollapsibleCard
                  detachId={nested.id}
                  title={nested.title}
                  icon={CARD_ICONS[nested.id]}
                  padding="pt-3"
                  collapsed={isCardCollapsed(nested.id)}
                  onToggleCollapsed={() => toggleCardCollapsed(nested.id)}
                >
                  {nested.content}
                </CollapsibleCard>
              )}
            </CollapsibleCard>
          ))
        )}
      </div>
    </CollapsibleCard>
  );
}

interface FreshnessClockProps {
  freshness: SOPFreshness;
}

function FreshnessClock({ freshness }: FreshnessClockProps) {
  return (
    <Tooltip
      text={freshness.label}
      colorModifier="info"
      position="right"
      nowrap
      lightHoverClass="hover:bg-onlight-line-3"
    >
      <ClockIcon
        className={clsx(
          'h-4 w-4 stroke-2',
          freshness.isRecent
            ? 'text-alert-500 dark:text-alert-400'
            : 'text-onlight-text-3 dark:text-ondark-text-3'
        )}
        aria-label={freshness.label}
      />
    </Tooltip>
  );
}

interface SOPTabsProps {
  activeTabId?: SOPTabId;
  onChange: (tab: SOPTabId) => void;
}

function SOPTabs({ activeTabId, onChange }: SOPTabsProps) {
  return (
    <div className="dark:border-ondark-line-2 border-onlight-line-3 border-b">
      <nav className="-mb-px flex px-4 pt-1" aria-label="SOP">
        {TAB_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={clsx(
              'focus-visible:ring-primary-500 min-w-0 border-b-2 px-2 py-2 text-center text-sm font-medium focus-visible:outline-none focus-visible:ring-2',
              item.id === SOPTabId.SITUATIONS ? 'flex-[1.7]' : 'flex-1',
              item.id === activeTabId
                ? 'border-primary-700 text-primary-700 dark:text-primary-400 dark:border-primary-400'
                : 'text-onlight-text-3 hover:text-onlight-text-2 hover:border-onlight-line-3 dark:text-ondark-text-2 dark:hover:text-ondark-text-1 border-transparent'
            )}
            aria-current={item.id === activeTabId ? 'page' : undefined}
            onClick={() => onChange(item.id)}
          >
            {item.name}
          </button>
        ))}
      </nav>
    </div>
  );
}

export default SOPCard;
